import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ProductCard from '../components/ProductCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetsPage = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const [sortBy, setSortBy] = useState('default');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`, {
          params: { category: 'set' }
        });
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const brands = useMemo(() => {
    const uniqueBrands = [...new Set(products.map(h => h.brand))];
    return ['all', ...uniqueBrands];
  }, [products]);

  const sortedProducts = useMemo(() => {
    let filtered = [...products];
    
    if (selectedBrand !== 'all') {
      filtered = filtered.filter(p => p.brand === selectedBrand);
    }

    switch (sortBy) {
      case 'price-asc':
        return filtered.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return filtered.sort((a, b) => b.price - a.price);
      case 'name-asc':
        return filtered.sort((a, b) => 
          getLocalizedText(a.name).localeCompare(getLocalizedText(b.name))
        );
      case 'name-desc':
        return filtered.sort((a, b) => 
          getLocalizedText(b.name).localeCompare(getLocalizedText(a.name))
        );
      default:
        return filtered;
    }
  }, [sortBy, selectedBrand, getLocalizedText, products]);

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">{t.nav.sets}</h1>
          <p className="text-zinc-400">{t.categories.setsDesc}</p>
          <div className="mt-3 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3 space-y-2">
            <p>{language === 'hu' ? 'A teljes vízipipa készlet megrendelése után a manager felveszi Önnel a kapcsolatot a készlet hitelesítéséről.' : 'After ordering a complete hookah set, our manager will contact you regarding stock verification.'}</p>
            <p>{language === 'hu' ? 'A vízipipák és a teljes készletű vízipipák külföldi raktárból érkeznek, ezért szállításuk általában 5-7 munkanapot vesz igénybe. Vegyes rendelés esetén (vízipipa és kiegészítők) a csomagot akár a vízipipa beérkezése után küldjük, vagy a kiegészítőket azonnal elküldjük, és a vízipipát a beérkezése után külön postázzuk.' : 'Hookahs and complete hookah sets are shipped from a foreign warehouse, so delivery usually takes 5-7 business days. For mixed orders (hookah and accessories), we may send the package once the hookah arrives, or ship the accessories immediately and send the hookah separately after it arrives.'}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8 p-4 bg-zinc-900 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">{t.filters.sortBy}:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="default" className="text-white hover:bg-zinc-700">{t.filters.all}</SelectItem>
                <SelectItem value="price-asc" className="text-white hover:bg-zinc-700">{t.filters.priceAsc}</SelectItem>
                <SelectItem value="price-desc" className="text-white hover:bg-zinc-700">{t.filters.priceDesc}</SelectItem>
                <SelectItem value="name-asc" className="text-white hover:bg-zinc-700">{t.filters.nameAsc}</SelectItem>
                <SelectItem value="name-desc" className="text-white hover:bg-zinc-700">{t.filters.nameDesc}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">{t.filters.brand}:</span>
            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
              <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {brands.map(brand => (
                  <SelectItem key={brand} value={brand} className="text-white hover:bg-zinc-700">
                    {brand === 'all' ? t.filters.all : brand}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-zinc-500 mx-auto mb-4" />
            <p className="text-zinc-500">{t.common.loading}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {sortedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {sortedProducts.length === 0 && (
              <div className="text-center py-20">
                <p className="text-zinc-500 text-lg">{t.common.noResults}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SetsPage;
